<!-- Page Header -->
<div class="page-header">
  <div class="header-content">
    <div class="header-main">
      <div class="header-icon">
        <i class="fa-solid fa-layer-group"></i>
      </div>
      <div class="header-text">
        <h1 class="page-title">Category Management</h1>
        <p class="page-description">Manage your categories and organize your inventory</p>
      </div>
    </div>
    <div class="header-actions">
      <button class="btn btn-outline-secondary"
              (click)="listCategory()"
              [disabled]="isLoadingCategories()">
        @if (isLoadingCategories()) {
          <i class="fa-solid fa-arrows-rotate fa-spin"></i>
          Loading...
        } @else {
          <i class="fa-solid fa-arrows-rotate"></i>
          Refresh
        }
      </button>
      <button class="btn btn-primary"
              (click)="create(content)"
              [disabled]="isLoadingCategories()">
        <i class="fa-solid fa-plus"></i>
        Add Category
      </button>
    </div>
  </div>
</div>

<!-- Search and Filters -->
<div class="filters-panel">
  <div class="search-section">
    <div class="search-box">
      <div class="search-input-wrapper">
        @if (searchLoading()) {
          <i class="fa-solid fa-arrows-rotate fa-spin search-icon"></i>
        } @else {
          <i class="fa-solid fa-magnifying-glass search-icon"></i>
        }
        <input type="text"
               class="search-input"
               [(ngModel)]="searchTerm"
               (input)="onSearch()"
               placeholder="Search categories..."
               [disabled]="isLoadingCategories()">
      </div>
    </div>
  </div>

  <div class="stats-section">
    <div class="stat-card">
      <div class="stat-icon">
        <i class="fa-solid fa-layer-group"></i>
      </div>
      <div class="stat-content">
        <div class="stat-number">{{ filteredCategories.length || 0 }}</div>
        <div class="stat-label">Total Categories</div>
      </div>
    </div>
  </div>
</div>

<!-- Alert Messages -->
@if (successMessage) {
  <div style="margin-bottom: 20px;">
    <ngb-alert #selfClosingAlert [type]="type" (closed)="successMessage = ''">
      <i class="fas fa-check-circle me-2"></i>
      {{ message }}
    </ngb-alert>
  </div>
}

<!-- Data Table Panel -->
<div class="data-panel">
  <div class="panel-header">
    <div class="panel-title">
      <div class="title-icon">
        <i class="fa-solid fa-table-list"></i>
      </div>
      <div class="title-content">
        <div class="title-text">Categories List</div>
        <div class="title-subtitle">All categories available in your inventory system</div>
      </div>
    </div>
  </div>

  <div class="panel-content">
    @if (isLoadingCategories()) {
      <!-- Loading State -->
      <div class="loading-state">
        <div class="loading-content">
          <div class="loading-spinner">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
          <div class="loading-text">
            <div class="loading-title">Loading Categories</div>
            <div class="loading-subtitle">Please wait while we fetch your categories...</div>
          </div>
        </div>
      </div>
    } @else {
      <!-- Table -->
      <div class="table-container">
        <table class="table">
          <thead>
            <tr>
              <th style="width: 70px;">Image</th>
              <th>Name</th>
              <th>Status</th>
              <th style="width: 200px; text-align: right; padding-right: 20px;">Actions</th>
            </tr>
          </thead>
          <tbody>
            @for(category of filteredCategories; track category.id) {
              <tr>
                <td>
                  <img [src]="category.image || 'https://placehold.co/50x50'"
                       [alt]="category.name"
                       loading="lazy"
                       style="width: 44px; height: 44px; border-radius: var(--radius-md); object-fit: cover; border: 1px solid var(--border);">
                </td>
                <td>
                  <div style="font-weight: 600; color: var(--text-primary); font-size: 13px;">{{ category.name }}</div>
                  <div style="font-size: 11px; color: var(--text-tertiary); margin-top: 2px;">ID: {{ category.id }}</div>
                </td>
                <td>
                  @if (category.disabled) {
                    <span class="badge badge-status-inactive badge-dot">Disabled</span>
                  } @else {
                    <span class="badge badge-status-active badge-dot">Active</span>
                  }
                </td>
                <td style="text-align: right; padding-right: 20px;">
                  <div style="display: flex; gap: 8px; justify-content: flex-end;">
                    <button class="btn btn-sm btn-outline-secondary"
                            (click)="edit(content2, category)"
                            [disabled]="isToggling()">
                      <i class="fa-solid fa-pen-to-square me-1"></i>
                      Edit
                    </button>
                    <button class="btn btn-sm"
                            [class]="category.disabled ? 'btn-outline-secondary' : 'btn-outline-secondary'"
                            [style.color]="category.disabled ? 'var(--success-text)' : 'var(--danger-text)'"
                            [style.border-color]="category.disabled ? 'var(--success-border)' : 'var(--danger-border)'"
                            (click)="disable(category)"
                            [disabled]="isToggling()">
                      @if (isToggling()) {
                        <i class="fa-solid fa-arrows-rotate fa-spin me-1"></i>
                      } @else {
                        @if(category.disabled) {
                          <i class="fa-solid fa-circle-check me-1"></i>
                        } @else {
                          <i class="fa-solid fa-ban me-1"></i>
                        }
                      }
                      {{ category.disabled ? 'Enable' : 'Disable' }}
                    </button>
                  </div>
                </td>
              </tr>
            } @empty {
              <tr>
                <td colspan="4">
                  <div class="empty-state">
                    <div class="empty-icon">
                      <i class="fa-solid fa-layer-group" style="font-size: 40px;"></i>
                    </div>
                    <div class="empty-content">
                      <h5>No Categories Found</h5>
                      <p>
                        @if (searchTerm) {
                          No categories match your search criteria.
                        } @else {
                          Start by creating your first category to organize your inventory.
                        }
                      </p>
                      @if (!searchTerm) {
                        <button class="btn btn-primary btn-sm" (click)="create(content)">
                          <i class="fa-solid fa-plus me-2"></i>Create First Category
                        </button>
                      }
                    </div>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  </div>
</div>

<!-- Create Category Modal -->
<ng-template #content let-modal>
  <div class="modal-header">
    <div style="display: flex; align-items: center; gap: 12px;">
      <button class="btn btn-sm btn-outline-secondary" (click)="modal.close('close-click')" style="border: none;">
        <i class="fa-solid fa-arrow-left"></i>
      </button>
      <div>
        <h4 class="modal-title" style="margin-bottom: 2px;">Create New Category</h4>
        <p style="font-size: 13px; color: var(--text-tertiary); margin: 0;">Add a new category to organize your inventory</p>
      </div>
    </div>
  </div>

  <div class="modal-body">
    @if (successMessage) {
      <div style="margin-bottom: 16px;">
        <ngb-alert #selfClosingAlert [type]="type" (closed)="successMessage = ''">
          <i class="fas fa-info-circle me-2"></i>
          {{ message }}
        </ngb-alert>
      </div>
    }

    <form [formGroup]="createCatForm">
      <!-- Category Name -->
      <div class="form-group">
        <label for="createName" class="form-label">
          <i class="fa-solid fa-tag me-1"></i>Category Name
        </label>
        <input type="text"
               id="createName"
               formControlName="name"
               class="form-control"
               placeholder="Enter category name"
               [disabled]="isCreating()">
        @if (createCatForm.get('name')?.invalid && createCatForm.get('name')?.touched) {
          <div class="invalid-feedback d-block">
            <i class="fa-solid fa-circle-exclamation me-1"></i>
            Category name is required
          </div>
        }
      </div>

      <!-- Image Upload Section -->
      <div class="form-group" style="margin-top: 16px;">
        <label class="form-label">
          <i class="fa-solid fa-image me-1"></i>Category Image
        </label>

        @if(uploadProgress()) {
          <div style="margin-bottom: 12px;">
            <ngb-progressbar [value]="uploadProgress()"
                            type="primary"
                            [striped]="true"
                            [animated]="true" />
            <div style="text-align: center; font-size: 12px; color: var(--text-secondary); margin-top: 4px;">Uploading... {{ uploadProgress() }}%</div>
          </div>
        }

        <div style="border: 2px dashed var(--border); border-radius: var(--radius-lg); padding: 20px; transition: all 0.2s; background: var(--bg-muted);"
             [class.upload-disabled]="isCreating()"
             [class.upload-success]="uploadSuccess"
             [class.upload-error]="uploadError"
             (drop)="onFileDrop($event)"
             (dragover)="onDragOver($event)">

          <div style="display: flex; align-items: center; gap: 16px;">
            <div style="flex-shrink: 0;">
              @if(imagePreview()) {
                <img [src]="imagePreview()" alt="Preview" style="width: 64px; height: 64px; border-radius: var(--radius-md); object-fit: cover; border: 1px solid var(--border);" />
              } @else {
                <div style="width: 64px; height: 64px; border-radius: var(--radius-md); background: var(--bg-inset); display: flex; align-items: center; justify-content: center;">
                  <i class="fa-solid fa-cloud-arrow-up" style="font-size: 24px; color: var(--text-tertiary);"></i>
                </div>
              }
            </div>

            <div style="flex: 1; min-width: 0;">
              @if(imageName()) {
                <div style="font-size: 13px; font-weight: 600; color: var(--text-primary);">Image Selected</div>
                <div style="font-size: 12px; color: var(--text-tertiary); margin-top: 2px;">
                  <span style="font-weight: 500;">{{ imageName() }}</span>
                  <span> ({{ fileSize() }} KB)</span>
                </div>
              } @else {
                <div style="font-size: 13px; font-weight: 600; color: var(--text-primary);">Upload Category Image</div>
                <div style="font-size: 12px; color: var(--text-tertiary); margin-top: 2px;">
                  Drag and drop an image here, or click to select
                </div>
              }

              <div style="display: flex; gap: 8px; margin-top: 10px;">
                <input type="file"
                       accept=".jpg, .jpeg, .png"
                       (change)="onFileChange($event)"
                       hidden
                       #fileInput
                       [disabled]="isCreating()" />

                <button type="button"
                        class="btn btn-sm btn-outline-secondary"
                        (click)="fileInput.click()"
                        [disabled]="isCreating()">
                  <i class="fa-solid fa-folder-open me-1"></i>
                  Select Image
                </button>

                @if(selectedFile || imagePreview()) {
                  <button type="button"
                          class="btn btn-sm btn-outline-secondary"
                          style="color: var(--danger-text); border-color: var(--danger-border);"
                          (click)="removeImage()"
                          [disabled]="isCreating()">
                    <i class="fa-solid fa-trash-can me-1"></i>
                    Remove
                  </button>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>

  <div class="modal-footer">
    <button type="button"
            class="btn btn-outline-secondary"
            (click)="modal.close('cancel')"
            [disabled]="isCreating()">
      Cancel
    </button>
    <button type="button"
            class="btn btn-primary"
            (click)="OnSubmit()"
            [disabled]="createCatForm.invalid || isCreating()">
      @if (isCreating()) {
        <i class="fa-solid fa-arrows-rotate fa-spin me-2"></i>
        Creating...
      } @else {
        <i class="fa-solid fa-plus me-2"></i>
        Create Category
      }
    </button>
  </div>
</ng-template>

<!-- Edit Category Modal -->
<ng-template #content2 let-modal>
  <div class="modal-header">
    <div style="display: flex; align-items: center; gap: 12px;">
      <button class="btn btn-sm btn-outline-secondary" (click)="modal.close('close-click')" style="border: none;">
        <i class="fa-solid fa-arrow-left"></i>
      </button>
      <div>
        <h4 class="modal-title" style="margin-bottom: 2px;">Edit Category</h4>
        <p style="font-size: 13px; color: var(--text-tertiary); margin: 0;">Update {{ data?.name }} category details</p>
      </div>
    </div>
  </div>

  <div class="modal-body">
    @if (successMessage) {
      <div style="margin-bottom: 16px;">
        <ngb-alert #selfClosingAlert [type]="type" (closed)="successMessage = ''">
          <i class="fas fa-info-circle me-2"></i>
          {{ message }}
        </ngb-alert>
      </div>
    }

    <form [formGroup]="updateCatForm">
      <!-- Category Name -->
      <div class="form-group">
        <label for="editName" class="form-label">
          <i class="fa-solid fa-tag me-1"></i>Category Name
        </label>
        <input type="text"
               id="editName"
               formControlName="name"
               class="form-control"
               placeholder="Enter category name"
               [disabled]="isUpdating()">
        @if (updateCatForm.get('name')?.invalid && updateCatForm.get('name')?.touched) {
          <div class="invalid-feedback d-block">
            <i class="fa-solid fa-circle-exclamation me-1"></i>
            Category name is required
          </div>
        }
      </div>

      <!-- Image Upload Section -->
      <div class="form-group" style="margin-top: 16px;">
        <label class="form-label">
          <i class="fa-solid fa-image me-1"></i>Category Image
        </label>

        @if(uploadProgress()) {
          <div style="margin-bottom: 12px;">
            <ngb-progressbar [value]="uploadProgress()"
                            type="primary"
                            [striped]="true"
                            [animated]="true" />
            <div style="text-align: center; font-size: 12px; color: var(--text-secondary); margin-top: 4px;">Uploading... {{ uploadProgress() }}%</div>
          </div>
        }

        <div style="border: 2px dashed var(--border); border-radius: var(--radius-lg); padding: 20px; transition: all 0.2s; background: var(--bg-muted);"
             [class.upload-disabled]="isUpdating()"
             [class.upload-success]="uploadSuccess"
             [class.upload-error]="uploadError"
             (drop)="onFileDrop($event)"
             (dragover)="onDragOver($event)">

          <div style="display: flex; align-items: center; gap: 16px;">
            <div style="flex-shrink: 0;">
              @if(imagePreview() || data?.image) {
                <img [src]="imagePreview() || data?.image" alt="Preview" style="width: 64px; height: 64px; border-radius: var(--radius-md); object-fit: cover; border: 1px solid var(--border);" />
              } @else {
                <div style="width: 64px; height: 64px; border-radius: var(--radius-md); background: var(--bg-inset); display: flex; align-items: center; justify-content: center;">
                  <i class="fa-solid fa-cloud-arrow-up" style="font-size: 24px; color: var(--text-tertiary);"></i>
                </div>
              }
            </div>

            <div style="flex: 1; min-width: 0;">
              @if(imageName()) {
                <div style="font-size: 13px; font-weight: 600; color: var(--text-primary);">New Image Selected</div>
                <div style="font-size: 12px; color: var(--text-tertiary); margin-top: 2px;">
                  <span style="font-weight: 500;">{{ imageName() }}</span>
                  <span> ({{ fileSize() }} KB)</span>
                </div>
              } @else {
                <div style="font-size: 13px; font-weight: 600; color: var(--text-primary);">Update Category Image</div>
                <div style="font-size: 12px; color: var(--text-tertiary); margin-top: 2px;">
                  @if (data?.image) {
                    Current image is displayed. Upload a new one to replace it.
                  } @else {
                    Drag and drop an image here, or click to select
                  }
                </div>
              }

              <div style="display: flex; gap: 8px; margin-top: 10px;">
                <input type="file"
                       accept=".jpg, .jpeg, .png"
                       (change)="onFileChange($event)"
                       hidden
                       #fileInput
                       [disabled]="isUpdating()" />

                <button type="button"
                        class="btn btn-sm btn-outline-secondary"
                        (click)="fileInput.click()"
                        [disabled]="isUpdating()">
                  <i class="fa-solid fa-folder-open me-1"></i>
                  {{ data?.image ? 'Change Image' : 'Select Image' }}
                </button>

                @if(selectedFile || imagePreview()) {
                  <button type="button"
                          class="btn btn-sm btn-outline-secondary"
                          style="color: var(--danger-text); border-color: var(--danger-border);"
                          (click)="removeImage()"
                          [disabled]="isUpdating()">
                    <i class="fa-solid fa-trash-can me-1"></i>
                    Remove
                  </button>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>

  <div class="modal-footer">
    <button type="button"
            class="btn btn-outline-secondary"
            (click)="modal.close('cancel')"
            [disabled]="isUpdating()">
      Cancel
    </button>
    <button type="button"
            class="btn btn-primary"
            (click)="OnEdit(data)"
            [disabled]="updateCatForm.invalid || isUpdating()">
      @if (isUpdating()) {
        <i class="fa-solid fa-arrows-rotate fa-spin me-2"></i>
        Updating...
      } @else {
        <i class="fa-solid fa-floppy-disk me-2"></i>
        Update Category
      }
    </button>
  </div>
</ng-template>
